import { useId, useState } from 'react';
import { TICKET_ROOT_CAUSES } from '@servwell/shared';
import { Notice } from '../ui/Badge';
import { CreateFooter } from '../create/CreateFooter';
import { field } from '../create/Field';
import { resolveMissing, rootCauseLabel } from './ticketView';

/**
 * Resolving asks what fixed it and why it went wrong (spec §2, Q1's root causes); both are
 * required, and the button says which is still missing ("Still needed: …").
 */
export function ResolveForm({
  pending,
  error,
  onCancel,
  onResolve,
}: {
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onResolve: (v: {
    resolution: string;
    rootCause: (typeof TICKET_ROOT_CAUSES)[number];
  }) => void;
}) {
  const [resolution, setResolution] = useState('');
  const [rootCause, setRootCause] = useState('');
  const reasonId = useId();
  const fixId = useId();
  const causeId = useId();
  const missing = resolveMissing({ resolution, rootCause });
  return (
    <div
      role="group"
      aria-label="Resolve this ticket"
      className="space-y-3 rounded-[var(--radius)] border border-border p-3"
    >
      {error ? (
        <Notice tone="danger" alert>
          {error}
        </Notice>
      ) : null}
      <label htmlFor={fixId} className="block text-xs font-medium text-muted-foreground">
        What fixed it
      </label>
      <textarea
        id={fixId}
        autoFocus
        className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
        value={resolution}
        onChange={(e) => setResolution(e.target.value)}
        placeholder="Replaced the trap seal and ran the water for ten minutes"
      />
      <label
        htmlFor={causeId}
        className="block text-xs font-medium text-muted-foreground"
      >
        Root cause
      </label>
      <select
        id={causeId}
        className={field}
        value={rootCause}
        onChange={(e) => setRootCause(e.target.value)}
      >
        <option value="">Choose a root cause</option>
        {TICKET_ROOT_CAUSES.map((r) => (
          <option key={r} value={r}>
            {rootCauseLabel(r)}
          </option>
        ))}
      </select>
      <CreateFooter
        label="Resolve ticket"
        pendingLabel="Resolving…"
        missing={missing}
        pending={pending}
        reasonId={reasonId}
        onSubmit={() =>
          onResolve({
            resolution: resolution.trim(),
            rootCause: rootCause as (typeof TICKET_ROOT_CAUSES)[number],
          })
        }
        cancel={{ label: 'Cancel', onCancel }}
      />
    </div>
  );
}
